import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import { 
  XMarkIcon, 
  PlusIcon, 
  PencilIcon,
  TrashIcon,
  TruckIcon
} from '@heroicons/react/24/outline';
import { truckTypesService } from '../../services/logistics/truck-types';

const TruckTypeManagementModal = ({ isOpen, onClose, companyId, onTruckTypesUpdated }) => {
  const { t, i18n } = useTranslation();
  const [truckTypes, setTruckTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingTruckType, setEditingTruckType] = useState(null);
  const [formData, setFormData] = useState({
    nameEn: '',
    nameAr: '',
    value: ''
  });

  const loadTruckTypes = async () => {
    try {
      setLoading(true);
      const types = await truckTypesService.getTruckTypes(companyId);
      setTruckTypes(types);
    } catch (error) {
      console.error('Error loading truck types:', error);
      toast.error(t('vehicles.failed_to_load_truck_types', 'Failed to load truck types'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && companyId) {
      loadTruckTypes();
    }
  }, [isOpen, companyId]);

  // Auto-generate value from English name
  const generateValue = (englishName) => {
    if (!englishName) return '';
    
    return englishName
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, '') // Remove special characters except spaces
      .replace(/\s+/g, '_') // Replace spaces with underscores
      .replace(/_+/g, '_') // Replace multiple underscores with single
      .replace(/^_|_$/g, ''); // Remove leading/trailing underscores
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const newData = { ...prev, [name]: value };
      
      // Auto-generate value when English name changes (for both new and edit)
      if (name === 'nameEn') {
        newData.value = generateValue(value);
      }
      
      return newData;
    });
  };

  // Check if value already exists
  const isValueDuplicate = (value, excludeId = null) => {
    return truckTypes.some(truckType => 
      truckType.value === value && 
      (!excludeId || truckType.id !== excludeId)
    );
  };

  const resetForm = () => {
    setFormData({
      nameEn: '',
      nameAr: '',
      value: ''
    });
    setEditingTruckType(null);
    setShowAddForm(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.nameEn || !formData.nameAr) {
      toast.error(t('vehicles.name_required', 'English and Arabic names are required'));
      return;
    }

    // Validate that value contains only Latin characters
    if (!/^[a-z0-9_]+$/.test(formData.value)) {
      toast.error(t('vehicles.invalid_value', 'Value must contain only Latin letters, numbers, and underscores'));
      return;
    }

    // Check for duplicate values
    if (isValueDuplicate(formData.value, editingTruckType?.id)) {
      toast.error(t('vehicles.duplicate_value', 'A truck type with this value already exists'));
      return;
    }

    try {
      const truckTypeData = {
        name: {
          en: formData.nameEn,
          ar: formData.nameAr
        },
        value: formData.value,
        companyId: companyId
      };

      if (editingTruckType) {
        // If editing a default type, save it as custom first
        if (editingTruckType.isDefault && !editingTruckType.id) {
          const newId = await truckTypesService.saveDefaultAsCustom(editingTruckType, companyId);
          await truckTypesService.updateTruckType(newId, truckTypeData);
        } else {
          await truckTypesService.updateTruckType(editingTruckType.id, truckTypeData);
        }
        toast.success(t('vehicles.truck_type_updated', 'Truck type updated successfully'));
      } else {
        await truckTypesService.addTruckType(truckTypeData);
        toast.success(t('vehicles.truck_type_created', 'Truck type created successfully'));
      }

      resetForm();
      loadTruckTypes();
      if (onTruckTypesUpdated) {
        onTruckTypesUpdated();
      }
    } catch (error) {
      console.error('Error saving truck type:', error);
      toast.error(t('vehicles.failed_to_save_truck_type', 'Failed to save truck type'));
    }
  };

  const handleEdit = (truckType) => {
    setEditingTruckType(truckType);
    setFormData({
      nameEn: truckType.name.en,
      nameAr: truckType.name.ar,
      value: truckType.value
    });
    setShowAddForm(true);
  };

  const handleDelete = async (truckType) => {
    if (window.confirm(t('vehicles.delete_truck_type_confirmation', 'Are you sure you want to delete this truck type?'))) {
      try {
        if (truckType.isDefault && !truckType.id) {
          // For default types without ID, we can't delete them from database
          // but we can remove them from the local list
          setTruckTypes(prev => prev.filter(t => !(t.isDefault && t.value === truckType.value)));
          toast.success(t('vehicles.truck_type_deleted', 'Truck type deleted successfully'));
        } else {
          await truckTypesService.deleteTruckType(truckType.id);
          toast.success(t('vehicles.truck_type_deleted', 'Truck type deleted successfully'));
        }
        
        loadTruckTypes();
        if (onTruckTypesUpdated) {
          onTruckTypesUpdated();
        }
      } catch (error) {
        console.error('Error deleting truck type:', error);
        toast.error(t('vehicles.failed_to_delete_truck_type', 'Failed to delete truck type'));
      }
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div className="flex items-center space-x-3">
            <TruckIcon className="h-6 w-6 text-blue-600" />
            <h2 className="text-xl font-semibold text-gray-900">
              {t('vehicles.truck_type_management', 'Truck Type Management')}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          {/* Add New Truck Type Button */}
          <div className="mb-6">
            <button
              onClick={() => setShowAddForm(true)}
              className="btn-primary flex items-center space-x-2"
            >
              <PlusIcon className="h-5 w-5" />
              <span>{t('vehicles.add_truck_type', 'Add Truck Type')}</span>
            </button>
          </div>

          {/* Add/Edit Form */}
          {showAddForm && (
            <div className="mb-6 p-4 bg-gray-50 rounded-lg">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {editingTruckType 
                  ? t('vehicles.edit_truck_type', 'Edit Truck Type')
                  : t('vehicles.add_new_truck_type', 'Add New Truck Type')
                }
              </h3>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="label">{t('vehicles.name_english', 'Name (English)')} *</label>
                    <input
                      type="text"
                      name="nameEn"
                      value={formData.nameEn}
                      onChange={handleInputChange}
                      className="input-field"
                      required
                      placeholder={t('vehicles.english_name_placeholder', 'e.g., Dina Refrigerator')}
                    />
                  </div>
                  <div>
                    <label className="label">{t('vehicles.name_arabic', 'Name (Arabic)')} *</label>
                    <input
                      type="text"
                      name="nameAr"
                      value={formData.nameAr}
                      onChange={handleInputChange}
                      className="input-field"
                      required
                      placeholder={t('vehicles.arabic_name_placeholder', 'e.g., دينا ثلاجة')}
                    />
                  </div>
                </div>
                <div>
                  <label className="label">{t('vehicles.value', 'Value')}</label>
                  <input
                    type="text"
                    name="value"
                    value={formData.value}
                    onChange={handleInputChange}
                    className="input-field"
                    placeholder={t('vehicles.value_placeholder', 'Auto-generated from English name')}
                    required
                  />
                  <p className="text-sm text-gray-500 mt-1">
                    {t('vehicles.value_description', 'Auto-generated from English name. Must contain only Latin letters, numbers, and underscores.')}
                  </p>
                </div>
                <div className="flex space-x-3">
                  <button type="submit" className="btn-primary">
                    {editingTruckType 
                      ? t('common.update', 'Update')
                      : t('common.add', t('common.add'))
                    }
                  </button>
                  <button
                    type="button"
                    onClick={resetForm}
                    className="btn-secondary"
                  >
                    {t('common.cancel', t('common.cancel'))}
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Truck Types List */}
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-2 text-gray-600">{t('common.loading', t('common.loading'))}</p>
            </div>
          ) : (
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900">
                {t('vehicles.truck_types', 'Truck Types')} ({truckTypes.length})
              </h3>
              {truckTypes.length === 0 ? (
                <p className="text-gray-500 text-center py-8">
                  {t('vehicles.no_truck_types_found', 'No truck types found')}
                </p>
              ) : (
                <div className="grid gap-4">
                  {truckTypes.map((truckType, index) => (
                    <div
                      key={truckType.id || index}
                      className="flex items-center justify-between p-4 bg-white border rounded-lg shadow-sm"
                    >
                      <div className="flex items-center space-x-4">
                        <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                          <TruckIcon className="h-5 w-5 text-blue-600" />
                        </div>
                        <div>
                          <div className="font-medium text-gray-900">
                            {i18n.language === 'ar' ? truckType.name.ar : truckType.name.en}
                          </div>
                          <div className="text-sm text-gray-500">
                            {i18n.language === 'ar' ? truckType.name.en : truckType.name.ar}
                          </div>
                          <div className="text-xs text-gray-400">
                            {truckType.value}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleEdit(truckType)}
                          className="p-2 text-gray-400 hover:text-blue-600"
                          title={t('common.edit', t('common.edit'))}
                        >
                          <PencilIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(truckType)}
                          className="p-2 text-gray-400 hover:text-red-600"
                          title={t('common.delete', t('common.delete'))}
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TruckTypeManagementModal; 